import Phaser from 'phaser';
import { HUD_FONT } from '../fonts';
import { fillIngredientFromInventory } from '../crafting/forge';
import {
  clearInventoryFilter,
  getInventoryDisplaySlots,
  tryEquipOnInventoryTap,
} from '../equipment';
import { exitCraftingMode, isForgeOpen } from '../crafting/mode';
import { hudViewport } from '../hud-viewport';
import {
  inventoryCraftingWindowRect,
  inventoryGridMetrics,
  inventoryWindowRect,
  type WinRect,
} from './ui-layout';
import { ensureItemTexture, fitItemIcon } from './item-textures';

const HUD_STYLE = {
  fontFamily: HUD_FONT,
  color: '#ffffff',
  stroke: '#000000',
  strokeThickness: 4,
};

const DEPTH_INVENTORY = 201;
const DEPTH_INVENTORY_SOLO = 200;

export function createInventoryModal(scene: Phaser.Scene, onRefreshEquipment?: () => void) {
  let visible = false;
  let activeWin: WinRect = inventoryWindowRect();

  const root = scene.add.container(0, 0).setScrollFactor(0).setDepth(DEPTH_INVENTORY_SOLO).setVisible(false);

  const backdrop = scene.add
    .rectangle(0, 0, 1, 1, 0x000000, 0.55)
    .setOrigin(0, 0)
    .setInteractive();

  const windowC = scene.add.container(activeWin.x, activeWin.y);
  const frameBg = scene.add
    .image(0, 0, 'ui-inventory-bg')
    .setOrigin(0, 0)
    .setDisplaySize(activeWin.w, activeWin.h);
  const frame = scene.add
    .image(0, 0, 'ui-inventory-frame')
    .setOrigin(0, 0)
    .setDisplaySize(activeWin.w, activeWin.h);
  const gridScroll = scene.add.container(0, 0);
  const windowBlocker = scene.add
    .rectangle(activeWin.w / 2, activeWin.h / 2, activeWin.w, activeWin.h, 0x000000, 0.001)
    .setInteractive();

  windowC.add([windowBlocker, frameBg, gridScroll, frame]);
  root.add([backdrop, windowC]);

  function closeModal() {
    visible = false;
    root.setVisible(false);
    backdrop.setVisible(true);
    root.setDepth(DEPTH_INVENTORY_SOLO);
    clearInventoryFilter();
    onRefreshEquipment?.();
  }

  function dismiss() {
    if (isForgeOpen()) {
      exitCraftingMode();
      return;
    }
    closeModal();
  }

  backdrop.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    pointer.event?.stopPropagation();
    dismiss();
  });

  async function buildGrid() {
    gridScroll.removeAll(true);
    const slots = getInventoryDisplaySlots();
    const { cellW, cellH, gap, columns, gridInset } = inventoryGridMetrics(activeWin);
    gridScroll.setPosition(gridInset, gridInset);

    for (let i = 0; i < slots.length; i++) {
      const { slotIndex, data } = slots[i];
      const col = i % columns;
      const row = Math.floor(i / columns);
      const x = col * (cellW + gap);
      const y = row * (cellH + gap);

      const cell = scene.add.container(x, y);
      const bg = scene.add.image(0, 0, 'ui-slot-square').setOrigin(0, 0).setDisplaySize(cellW, cellH);

      if (data?.count) {
        const icon = scene.add.image(cellW / 2, cellH / 2, 'ui-slot-square');
        fitItemIcon(icon, cellW, 0.72);
        void ensureItemTexture(scene, data.name).then((tex) => {
          if (tex && icon.active) {
            icon.setTexture(tex);
            fitItemIcon(icon, cellW, 0.72);
          }
        });

        const badge = scene.add
          .text(cellW - 12, cellH - 10, String(data.count), { ...HUD_STYLE, fontSize: '28px' })
          .setOrigin(1, 1);

        const hit = scene.add
          .rectangle(cellW / 2, cellH / 2, cellW, cellH, 0x000000, 0.001)
          .setInteractive({ useHandCursor: true });

        hit.on('pointerdown', () => {
          if (isForgeOpen()) {
            if (fillIngredientFromInventory(slotIndex)) {
              void rebuild();
              onRefreshEquipment?.();
            }
            return;
          }
          if (tryEquipOnInventoryTap(slotIndex)) {
            void rebuild();
            onRefreshEquipment?.();
          }
        });

        cell.add([bg, icon, badge, hit]);
      } else {
        cell.add(bg);
      }

      gridScroll.add(cell);
    }
  }

  function applyWindow(win: WinRect) {
    activeWin = win;
    backdrop.setPosition(0, 0).setSize(hudViewport.width, hudViewport.height);
    windowC.setPosition(win.x, win.y);
    frameBg.setDisplaySize(win.w, win.h);
    frame.setDisplaySize(win.w, win.h);
    windowBlocker.setPosition(win.w / 2, win.h / 2);
    windowBlocker.setSize(win.w, win.h);
  }

  async function rebuild() {
    await buildGrid();
  }

  return {
    root,
    open(opts?: { craftingLayout?: boolean }) {
      const crafting = Boolean(opts?.craftingLayout);
      applyWindow(crafting ? inventoryCraftingWindowRect() : inventoryWindowRect());
      backdrop.setVisible(!crafting);
      root.setDepth(crafting ? DEPTH_INVENTORY : DEPTH_INVENTORY_SOLO);
      if (crafting && root.parentContainer) {
        root.parentContainer.bringToTop(root);
      }
      visible = true;
      root.setVisible(true);
      void rebuild();
    },
    close: closeModal,
    isOpen: () => visible,
    refresh: rebuild,
    relayout() {
      if (!visible) return;
      applyWindow(isForgeOpen() ? inventoryCraftingWindowRect() : inventoryWindowRect());
      void rebuild();
    },
  };
}
